import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estimateExportSize, exportHtml } from './htmlExporter.ts';
import { showToast } from '@/components/Toast.tsx';
import styles from './ExportDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

function formatMB(mb: number): string {
  if (mb < 0.01) return '< 0.01 MB';
  return `${mb.toFixed(2)} MB`;
}

/** Standalone content (no backdrop) — used by ExportHubDialog */
export function ExportDialogContent({ onClose }: { onClose: () => void }) {
  const [includeVideo, setIncludeVideo] = useState(false);
  const [includeDoodles, setIncludeDoodles] = useState(false);
  const [compressImages, setCompressImages] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [sizes, setSizes] = useState<{ thumbnailsMB: number; audioMB: number; videoMB: number; totalMB: number } | null>(null);
  const { t } = useTranslation('export');

  useEffect(() => {
    // Measuring file:// thumbnails is no longer instant, so a fast toggle can land an older
    // result last — drop replies from a superseded run.
    let cancelled = false;
    estimateExportSize(includeVideo, compressImages).then(s => { if (!cancelled) setSizes(s); });
    return () => { cancelled = true; };
  }, [includeVideo, compressImages]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportHtml({ includeVideo, compressImages, includeDoodles });
      showToast(t('htmlExportedToast'));
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {sizes && (
        <table className={styles.sizeTable}>
          <tbody>
            <tr>
              <td>{t('sizeLabelThumbnails')}</td>
              <td>{formatMB(sizes.thumbnailsMB)}</td>
            </tr>
            <tr>
              <td>{t('sizeLabelAudio')}</td>
              <td>{formatMB(sizes.audioMB)}</td>
            </tr>
            {sizes.videoMB > 0 && includeVideo && (
              <tr>
                <td>{t('sizeLabelVideo')}</td>
                <td>{formatMB(sizes.videoMB)}</td>
              </tr>
            )}
            <tr className={styles.total}>
              <td>{t('sizeLabelEstimatedTotal')}</td>
              <td>{formatMB(sizes.totalMB)}</td>
            </tr>
          </tbody>
        </table>
      )}

      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={compressImages}
          onChange={e => setCompressImages(e.target.checked)}
        />
        {t('checkboxCompressThumbnails')}
        <span className={styles.sizeHint}>{t('compressHint')}</span>
      </label>

      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={includeVideo}
          onChange={e => setIncludeVideo(e.target.checked)}
        />
        {t('checkboxIncludeVideoShots')}
        {sizes && sizes.videoMB > 0 && (
          <span className={styles.sizeHint}>+{formatMB(sizes.videoMB)}</span>
        )}
      </label>

      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={includeDoodles}
          onChange={e => setIncludeDoodles(e.target.checked)}
        />
        {t('checkboxIncludeDoodles')}
      </label>

      <div className={styles.actions}>
        <button className={styles.cancelBtn} onClick={onClose} disabled={isExporting}>
          {t('cancelButton')}
        </button>
        <button className={styles.exportBtn} onClick={handleExport} disabled={isExporting}>
          {isExporting ? t('exportingButton') : t('exportButton')}
        </button>
      </div>
    </>
  );
}

/** Standalone dialog with backdrop — backward compatible */
export function ExportDialog({ open, onClose }: Props) {
  const { t } = useTranslation('export');
  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.title}>{t('exportDialogTitle')}</div>
        <ExportDialogContent onClose={onClose} />
      </div>
    </div>
  );
}
